/**
 * VendorMemoryDialog — read-only widok pamięci Agenta Faktur dla wszystkich kontrahentów.
 *
 * Otwierany z poziomu Ustawienia → Agenci AI → Agent Faktur → przycisk "Pamięć kontrahentów".
 *
 * UI:
 *  - Lista vendorów: NIP, nazwa, last_analysis, payment_type, liczba notatek
 *  - Po kliknięciu wiersza: szczegóły z metadanymi źródeł (KSeF / użytkownik / system)
 *  - Sekcja "Dane przekazane do analizy" — transparentność (co trafia do LLM)
 *
 * Nie wywołuje LLM. Tylko czyta z `agent_vendor_memory` przez 2 endpointy:
 *  - GET /api/ai/agents/invoice/memory
 *  - GET /api/ai/agents/invoice/memory/:nip
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain,
  Loader2,
  Trash2,
  ArrowLeft,
  Database,
  User as UserIcon,
  Server,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { logger } from "../../utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_LABEL = {
  przelew: "Przelew",
  gotowka: "Gotówka",
  karta: "Karta",
  mieszane: "Mieszane",
  do_decyzji: "Do decyzji",
};

const formatPLN = (n) =>
  new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(Number(n || 0));

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
};

const SourceBadge = ({ source }) => {
  const map = {
    użytkownik: { icon: UserIcon, className: "bg-blue-50 text-blue-700 border-blue-200" },
    KSeF:       { icon: Database, className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    system:     { icon: Server, className: "bg-gray-100 text-gray-700 border-gray-200" },
  };
  const cfg = map[source] || map.system;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${cfg.className}`}>
      <Icon className="w-3 h-3" /> {source}
    </span>
  );
};

const VendorMemoryDialog = ({ open, onOpenChange }) => {
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedNip, setSelectedNip] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedNip(null);
      setDetail(null);
      return;
    }
    fetchList();
  }, [open]);

  const fetchList = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/ai/agents/invoice/memory?limit=200`, { withCredentials: true });
      setList(res.data?.vendors || []);
    } catch (e) {
      logger.error("memory list error", e);
      toast.error("Nie udało się pobrać pamięci agenta");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetail = async (nip) => {
    setSelectedNip(nip);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const res = await axios.get(
        `${API}/ai/agents/invoice/memory/${encodeURIComponent(nip)}`,
        { withCredentials: true }
      );
      setDetail(res.data);
    } catch (e) {
      logger.error("memory detail error", e);
      toast.error("Nie udało się pobrać szczegółów");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (nip) => {
    if (!window.confirm(`Wyczyścić pamięć kontrahenta ${nip}? Operacja jest nieodwracalna.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/ai/agents/invoice/memory/${encodeURIComponent(nip)}`, { withCredentials: true });
      toast.success("Pamięć wyczyszczona");
      setSelectedNip(null);
      setDetail(null);
      fetchList();
    } catch (e) {
      logger.error("memory delete error", e);
      toast.error("Nie udało się usunąć");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[92vh] flex flex-col" data-testid="vendor-memory-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-200">
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-600" />
            Pamięć kontrahentów — Agent Faktur
          </DialogTitle>
          <DialogDescription className="text-xs">
            Read-only podgląd zapisanych informacji. Każda wartość ma znacznik źródła.
          </DialogDescription>
        </DialogHeader>

        {/* Widok listy */}
        {!selectedNip && (
          <div className="flex-1 overflow-y-auto" data-testid="vendor-memory-list">
            {loadingList && (
              <div className="p-10 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie…
              </div>
            )}
            {!loadingList && list.length === 0 && (
              <div className="p-10 text-center text-gray-500 text-sm">
                Pamięć Agenta jest pusta. Po pierwszej analizie pojawią się tu kontrahenci.
              </div>
            )}
            {!loadingList && list.map((v) => (
              <button
                key={v.vendor_nip}
                onClick={() => fetchDetail(v.vendor_nip)}
                data-testid={`vendor-memory-row-${v.vendor_nip}`}
                className="w-full text-left px-6 py-3 border-b border-gray-100 hover:bg-blue-50/40 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {v.vendor_name || "(brak nazwy)"}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">{v.vendor_nip}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-xs">
                    {v.payment_type && (
                      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {PAYMENT_LABEL[v.payment_type] || v.payment_type}
                      </span>
                    )}
                    {v.notes_count > 0 && (
                      <span className="text-gray-500">
                        {v.notes_count} {v.notes_count === 1 ? "notatka" : "notatek"}
                      </span>
                    )}
                  </div>
                </div>
                {v.last_analysis_at && (
                  <div className="mt-1.5 text-xs text-gray-500 flex flex-wrap gap-x-3">
                    <span>Ostatnia analiza: <strong>{formatDate(v.last_analysis_at)}</strong></span>
                    <span>{v.last_analysis_invoice_count} faktur</span>
                    <span className="font-mono">{formatPLN(v.last_analysis_total_gross)}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Widok szczegółów */}
        {selectedNip && (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="vendor-memory-detail">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedNip(null); setDetail(null); }}
              data-testid="vendor-memory-back-btn"
              className="h-9 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Wróć do listy
            </Button>

            {loadingDetail && (
              <div className="text-center text-gray-500 text-sm flex items-center justify-center gap-2 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie…
              </div>
            )}

            {detail && (
              <>
                <div>
                  <div className="font-semibold text-gray-900">{detail.vendor_name || "(brak nazwy)"}</div>
                  <div className="text-xs text-gray-500 font-mono">{detail.vendor_nip}</div>
                </div>

                {/* Zapisane informacje + źródła */}
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                    Zapisane informacje
                  </div>
                  {detail.fields?.length === 0 && (
                    <div className="text-sm text-gray-500 italic">Brak zapisanych wartości.</div>
                  )}
                  {detail.fields?.map((f) => (
                    <div key={f.key} className="flex items-start justify-between gap-2 bg-gray-50 rounded px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500">{f.label}</div>
                        <div className="text-sm text-gray-900 break-words">
                          {f.value_label || f.value}
                        </div>
                      </div>
                      <SourceBadge source={f.source} />
                    </div>
                  ))}
                </div>

                {/* Ostatnia analiza */}
                {detail.last_analysis && (
                  <div className="border-t border-gray-200 pt-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                      Ostatnia analiza
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-gray-500">Data</div>
                        <div>{formatDate(detail.last_analysis.at)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Faktury</div>
                        <div className="font-semibold">{detail.last_analysis.invoice_count}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Kwota</div>
                        <div className="font-mono">{formatPLN(detail.last_analysis.total_gross)}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 font-mono break-all">
                      fingerprint: {detail.last_analysis.fingerprint}
                    </div>
                  </div>
                )}

                {/* Dane przekazane do LLM */}
                {detail.payload_meta && (
                  <div className="border-t border-gray-200 pt-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Dane przekazane do analizy LLM
                    </div>
                    <div className="bg-blue-50/50 border border-blue-100 rounded px-3 py-2 text-sm space-y-1">
                      <div>
                        <span className="text-gray-500">Liczba faktur: </span>
                        <span className="font-medium">{detail.payload_meta.invoice_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Łączna kwota: </span>
                        <span className="font-mono font-medium">
                          {formatPLN(detail.payload_meta.total_gross)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Czy pełne faktury: </span>
                        <span className={`font-medium ${detail.payload_meta.full_invoices_sent_to_llm ? "text-amber-700" : "text-emerald-700"}`}>
                          {detail.payload_meta.full_invoices_sent_to_llm ? "TAK" : "NIE (tylko agregaty)"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Liczba znaków payload: </span>
                        <span className="font-mono font-medium">~{detail.payload_meta.approx_payload_chars}</span>
                      </div>
                      <div className="pt-1">
                        <span className="text-gray-500">Pola wysyłane do LLM: </span>
                        <span className="font-mono text-xs">
                          {(detail.payload_meta.fields_sent_to_llm || []).join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete */}
                <div className="border-t border-gray-200 pt-3">
                  <Button
                    variant="outline"
                    onClick={() => handleDelete(detail.vendor_nip)}
                    data-testid="vendor-memory-delete-btn"
                    className="text-red-600 border-red-200 hover:bg-red-50 h-10 sm:h-9"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Wyczyść pamięć tego kontrahenta
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VendorMemoryDialog;
